import Vue from 'vue';
import { Component } from 'nuxt-property-decorator';
import { MetaInfo } from 'vue-meta';
import HotelViewRoot from '../../-index';
import { MyHotelLocalNavi } from '../-components';
import {
  HTimeline,
  HSimpleCoverTitle,
  HTabs,
  HTab,
  HContentSwitcher,
  HThemeDecoratedTitle,
  HBtn,
  HIconLink,
  HIcon,
  HLink,
} from '~/components';
import { HotelTodoPattern, getMediaDerived } from '~/schemes';

// eslint-disable-next-line no-undef
type JSXElement = VueTsxSupport.JSX.Element;

@Component<MyView>({
  inject: ['hotelViewRoot'],
  scrollBehaviorPosition(ctx, to, from) {
    if (to.name === from.name) {
      return false;
    } else {
      return { x: 0, y: 0 };
    }
  },
  async middleware({ $hotel, params, redirect }) {
    const hotel = await $hotel.getBySlug(params.hotel_slug);
    if (hotel && !hotel.todo) {
      /**
       * TODO設定がなければ施設TOPにリダイレクトしてあげる
       */
      const location = $hotel.location(undefined, hotel);
      return redirect(location);
    }
  },
  head() {
    const { hotel, todo, title } = this;
    const { description } = todo;
    const head: MetaInfo = {
      title,
      meta: [
        { hid: 'og:title', property: 'og:title', content: title },
        {
          hid: 'og:url',
          property: 'og:url',
          content: `${this.$navigation.origin}/${this.$language.current}/hotels/${hotel.slug}/todo/${this.slug}/`,
        },
        {
          hid: 'og:image',
          property: 'og:image',
          content: this.$res.img(todo.ogImage),
        },
      ],
    };

    if (description) {
      head.meta!.push(
        {
          hid: 'description',
          name: 'description',
          content: description,
        },
        {
          hid: 'og:description',
          property: 'og:description',
          content: description,
        },
      );
    }
    return head;
  },
  mounted() {
    window.addEventListener('scroll', this.scrollAnimation);
    this.scrollAnimation(); // 初回実行
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.scrollAnimation);
  },
  render() {
    const {
      title,
      titleDeco,
      subTitle,
      mainVisual,
      tabSettings,
      tabs,
      patterns,
      nextTab,
    } = this;

    // 星のやの場合
    if (this.$theme.is('hoshinoya')) {
      return (
        <div staticClass="my-hotel-todo-index-view">
          <MyHotelLocalNavi />
          <div staticClass="my-inner">
            <h2
              staticClass="my-inner__title"
              v-wysiwyg={`${this.hotel.name}<br/>${this.$t(
                'label.story',
              )}`}></h2>

            <div staticClass="my-stories">
              {this.getStories().map((story) => story)}
            </div>
          </div>
        </div>
      );
    }

    // LUCYの場合
    if (this.$theme.is('lucy')) {
      return (
        <div staticClass="my-hotel-todo-index-view">
          <MyHotelLocalNavi />
          <div staticClass="my-inner">
            <HSimpleCoverTitle
              title={title}
              deco={titleDeco}
              subTitle={subTitle}
              image={mainVisual}
            />

            <div staticClass="my-contents">
              {/* 目次 */}
              <div staticClass="my-contents__index-list">
                {patterns.map(({ patternName }, index) => (
                  <HLink staticClass="my-contents__index" href={`#${index}`}>
                    <HIcon name="keyboard-arrow-down" />
                    <span>{patternName}</span>
                  </HLink>
                ))}
              </div>

              {patterns.map(({ patternName, days }, index) => (
                <div>
                  <h2 staticClass="my-contents__title" id={index.toString()}>
                    {patternName}
                  </h2>

                  {days.map(({ schedules }, dayIndex) => (
                    <div staticClass="my-contents__day">
                      <h3 staticClass="my-contents__day__title">
                        {this.$t('value.dailyLabel', {
                          n: dayIndex + 1,
                        })}
                      </h3>
                      <HTimeline
                        staticClass="my-contents__timeline"
                        items={schedules}
                      />
                    </div>
                  ))}
                </div>
              ))}

              {/* 各見出しへジャンプするボタン (SPでのみ表示) */}
              <div staticClass="my-next my-dynamic-container">
                {patterns.map(({ patternName }, index) => (
                  <HLink staticClass="my-next__btn" href={`#${index}`}>
                    <HBtn color="skelton">{patternName}</HBtn>
                  </HLink>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div staticClass="my-hotel-todo-index-view">
        <MyHotelLocalNavi />
        <div staticClass="my-inner">
          <HSimpleCoverTitle
            title={title}
            deco={titleDeco}
            subTitle={subTitle}
            image={mainVisual}
          />

          {/* パタンが二つの場合はタブで表示する */}
          {tabSettings.length === 2 && (
            <HTabs staticClass="my-tabs" v-model={this.slug} sticky="narrow">
              {tabSettings.map((tab) => (
                <HTab id={tab.slug} key={tab.slug}>
                  {tab.label}
                </HTab>
              ))}
            </HTabs>
          )}

          <div staticClass="my-contents">
            {(() => {
              const scopedSlots = {};
              patterns.forEach((pattern, patternIndex) => {
                const wideImage = getMediaDerived('wide', pattern.image, '');
                const narrowImage = getMediaDerived(
                  'narrow',
                  pattern.image,
                  '',
                );
                // 画像はwideとnarrow両方あるときだけ表示したいため
                const imageStyles =
                  wideImage && narrowImage
                    ? {
                        '--image-wide': `url(${wideImage})`,
                        '--image-narrow': `url(${narrowImage})`,
                      }
                    : undefined;
                const key = tabs[patternIndex];
                scopedSlots[key] = () => (
                  <div>
                    {imageStyles && (
                      <div class="my-content__image" style={imageStyles}></div>
                    )}
                    {/** 説明文 **/}
                    {pattern.description && (
                      <div
                        v-wysiwyg={pattern.description}
                        staticClass="h-theme-lead my-description"
                      />
                    )}
                    {pattern.days.map((day, index) => {
                      return (
                        <div key={day.key || index}>
                          {/** ○日目と3点ドット **/}
                          <HThemeDecoratedTitle
                            staticClass="my-theme-decorated-title"
                            size="md"
                            tag="h3">
                            {this.$t('value.dailyLabel', {
                              n: index + 1,
                            })}
                          </HThemeDecoratedTitle>
                          {/** スケジュール **/}
                          <HTimeline
                            key={key}
                            staticClass="my-content"
                            items={day.schedules}
                            stack={this.$theme.is('kai')}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              });
              return (
                <HContentSwitcher
                  value={this.slug}
                  order={tabs}
                  autotop={{
                    duration: 0,
                    offset: -100,
                  }}
                  scopedSlots={scopedSlots}
                />
              );
            })()}

            {!!nextTab && (
              <div staticClass="my-next my-dynamic-container">
                <HBtn
                  staticClass="my-next__btn"
                  block={!this.$theme.is('kai')}
                  to={nextTab.location}
                  color="plain">
                  {nextTab.nextLabel}
                </HBtn>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
})
export default class MyView extends Vue {
  readonly hotelViewRoot!: HotelViewRoot;

  /**
   * タイトルのデコレーション
   *
   * * リゾナーレの時だけ、欧文テキストを記載する
   *
   * @see https://hoshinoresorts-dev.slack.com/archives/C01PKFF4753/p1668563143395969
   */
  get titleDeco() {
    if (!this.$theme.is('risonare') || this.$language.current === 'en') {
      return;
    }
    return 'How to Spend';
  }

  get hotel() {
    return this.hotelViewRoot.hotel;
  }

  get title() {
    // 界はナビは「過ごしかた」だが下層タイトルは「温泉満喫のすすめ」
    if (this.$theme.is('kai')) {
      return this.$t('label.todoKai') as string;
    }
    // 界以外はセクション名を使う
    const { sections } = this.$theme.current;
    const todoSection = sections.find(({ key }) => key === 'todo');
    if (todoSection) {
      return todoSection.label as string;
    } else {
      return this.$t('label.todo') as string;
    }
  }

  get subTitle() {
    if (!this.mainVisual) return;
    return this.todo.subTitle;
  }

  get mainVisual() {
    return this.todo.mainVisual;
  }

  get slug() {
    return this.$route.params.todo_slug;
  }

  set slug(slug: string) {
    if (this.$route.params.todo_slug !== slug && this.tabs.includes(slug)) {
      this.$router.push(this.$hotel.location(`/todo/${slug}`));
    }
  }

  get todo() {
    const { todo } = this.hotel;
    if (!todo) {
      throw new Error('missing todo');
    }
    return todo;
  }

  get hotelLocationPath() {
    return this.$hotel.location('/', this.hotel).path || '';
  }

  get patterns(): HotelTodoPattern[] {
    const { hotelLocationPath } = this;

    return this.todo.patterns.map((pattern) => {
      const days = pattern.days.map((day) => {
        const schedules = day.schedules.map((schedule) => {
          const { image, link } = schedule;
          return {
            ...schedule,
            image: (image && this.$res.img(image)) || null,
            link:
              (link && {
                ...link,
                url: link.url.replace(/^~\/?/, hotelLocationPath),
              }) ||
              null,
          };
        });
        return { ...day, schedules };
      });
      return { ...pattern, days };
    });
  }

  get tabSettings() {
    return this.todo.patterns.map((pattern, index) => {
      const label = pattern.patternName;
      return {
        label,
        slug: String(index + 1),
        nextLabel: this.$t('value.viewAt', { target: label }),
      };
    });
  }

  get tabs() {
    return this.tabSettings.map(({ slug }) => slug);
  }

  get currentIndex() {
    const { slug } = this;
    return this.tabs.findIndex((tab) => tab === slug);
  }

  get nextTabIndex() {
    /* 次のタブへ順繰り導線が作られるようなインデックス処理 */
    return (this.currentIndex + 1) % this.tabSettings.length;
  }

  get nextTab() {
    if (this.tabSettings.length < 2) return;

    const nextSetting = this.tabSettings[this.nextTabIndex];

    if (nextSetting) {
      return {
        ...nextSetting,
        location: this.$hotel.location(`/todo/${nextSetting.slug}`, this.hotel),
      };
    }
  }

  /**
   * スクロール量に応じたアニメーションを実行
   */
  private scrollAnimation() {
    const stories = document.querySelectorAll('.my-story');
    stories.forEach((story) => {
      // すでにアニメーションが完了している場合はスキップ
      if (story.classList.contains('hasAnimated')) return;

      const storyPosition = story.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // ビューポート外ならスキップ
      if (storyPosition.top > windowHeight || storyPosition.bottom < 0) return;

      // アニメーション範囲の設定
      const animationStartPoint = windowHeight * 0.85; // 画面上側から85%の位置でアニメーション開始
      const animationEndPoint = windowHeight * 0.45; // 画面上側から45%の位置でアニメーション終了
      const animationProgress = // アニメーションの進捗度合いを算出（マイナスや1以上になり得る）
        (storyPosition.top - animationStartPoint) /
        (animationEndPoint - animationStartPoint);

      // 進捗度合いを0～1の範囲で表現
      const constrainedProgress = Math.min(Math.max(animationProgress, 0), 1);

      // スクロール量に比例してスタイルを更新
      const horizontalInset = 50 - constrainedProgress * 50;
      const verticalInset = 50 - constrainedProgress * 50;
      // ストーリーの左上または右上にカードが表示されるよう-5%の余白を設定
      (story as HTMLElement).style.clipPath = `inset(${verticalInset - 5}% ${
        horizontalInset - 5
      }% ${verticalInset - 5}% ${horizontalInset - 5}%)`;
      (story as HTMLElement).style.opacity = String(constrainedProgress);

      // アニメーションが完了したらそれを示すclassを付与
      if (constrainedProgress === 1) {
        story.classList.add('hasAnimated');
      }
    });
  }

  /**
   * リンクのテキストを返却
   * @param text スケジュールのリンクに設定されたテキスト
   * - 未設定: 詳細
   * - 上記以外: スケジュールのリンクに設定されたテキスト文字列
   */
  private getLinkText(text: string): string {
    return text || (this.$t('label.details') as string);
  }

  /** 星のやのストーリー構成要素を返却 */
  private getStories(): JSXElement[] {
    const storyNodes: JSXElement[] = [];
    // 星のやのストーリーは1パターンのみなので
    // 最初の配列を取得する
    const firstPattern = this.patterns[0];
    // 星のやのストーリー(スケジュール)は1日目のみなので
    // 最初の配列を取得する
    const stories = firstPattern.days[0].schedules;
    const [storyTop, ...otherStories] = stories;
    const { description, link, image } = storyTop;
    // ストーリーのトップ要素を取得
    const storyTopNode = (
      <div staticClass="my-story-top">
        {description && (
          <div
            staticClass="my-story-top__description"
            v-wysiwyg={description}></div>
        )}

        {link && (
          <HIconLink
            staticClass="my-story-top__detail"
            icon="arrow-right"
            props={{ ...this.$navigation.resolveHrefTo(link.url) }}
            hiddenExternalIcon>
            {this.getLinkText(link.text)}
          </HIconLink>
        )}

        {image && <img staticClass="my-story-top__image" src={image} alt="" />}
      </div>
    );
    storyNodes.push(storyTopNode);
    // ストーリーのトップ以外の要素を取得
    otherStories.forEach(({ image, description, link }) => {
      const otherStoryNode = (
        <div
          staticClass="my-story revealing-story"
          style={`--image-url: url(${image})`}>
          <div staticClass="my-story-detail">
            {description && (
              <div
                staticClass="my-story-detail__description"
                v-wysiwyg={description}></div>
            )}
            {link && (
              <HIconLink
                staticClass="my-story-detail__link"
                icon="arrow-right"
                props={{ ...this.$navigation.resolveHrefTo(link.url) }}
                hiddenExternalIcon>
                {this.getLinkText(link.text)}
              </HIconLink>
            )}
          </div>
        </div>
      );
      storyNodes.push(otherStoryNode);
    });
    return storyNodes;
  }
}
